import {useEffect, useState} from "react";

import {AsyncSearchCombobox} from "@/components/ui/async-search-combobox";

import {ParamsOptions} from "../../core/types";
import {useHouseholds} from "../hooks/use-households";
import {
  HOUSEHOLD_SEARCH_DEBOUNCE_MS,
  householdComboboxEmptyMessage,
  householdComboboxIsLoading,
  nextHouseholdComboboxFilter,
} from "../lib/household-search";
import {getHouseholdResidentDisplayName} from "../lib/household-membership";
import {Household} from "../types";
import {HouseholdComboboxItem} from "./household-combobox-item";

/** Form/list selections often only carry id + name until a full household is loaded. */
export type HouseholdComboboxValue = Pick<Household, "house_hold_id" | "name"> &
  Partial<Omit<Household, "house_hold_id" | "name">>;

function getHouseholdComboboxLabel(household: HouseholdComboboxValue): string {
  const assignee = getHouseholdResidentDisplayName(household);
  return assignee !== "—" ? `${household.name} · ${assignee}` : household.name;
}

type HouseholdComboboxProps = {
  value: HouseholdComboboxValue | null;
  onChange: (value: HouseholdComboboxValue | null) => void;
  filter?: ParamsOptions["filter"];
  disabled?: boolean;
  className?: string;
};

export function HouseholdCombobox({
  value,
  filter,
  onChange,
  disabled,
  className,
}: HouseholdComboboxProps) {
  const getBaseFilter = () => ({...(filter ?? {})});

  // Parents often pass `filter={{ context_property_id: id }}` inline — new object every render.
  // Compare serialized scope so we do not wipe the active search `q` on unrelated re-renders.
  const filterScopeKey = JSON.stringify(filter ?? {});

  const [options, setOptions] = useState<ParamsOptions>({
    sort: "-created_at",
    filter: getBaseFilter(),
  });
  const [activeSearch, setActiveSearch] = useState("");

  const query = useHouseholds(options, {enabled: !disabled});
  const {households, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage} = query;

  const handleSearch = (term: string) => {
    setActiveSearch(term);
    const q = term.trim();

    setOptions((old) => {
      const base = getBaseFilter();
      const {changed, filter} = nextHouseholdComboboxFilter(
        old.filter as Record<string, unknown> | undefined,
        base,
        q,
      );
      if (!changed) {
        return old;
      }
      return {...old, filter};
    });
  };

  useEffect(() => {
    setOptions((old) => ({
      ...old,
      filter: getBaseFilter(),
    }));
    setActiveSearch("");
  }, [filterScopeKey]);

  const listLoading = householdComboboxIsLoading(activeSearch, isFetching);
  const emptyMessage = householdComboboxEmptyMessage(activeSearch);

  return (
    <AsyncSearchCombobox<HouseholdComboboxValue>
      items={households}
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      onLoadMore={fetchNextPage}
      hasMore={hasNextPage}
      isLoading={listLoading}
      isLoadingMore={isFetchingNextPage}
      debounceMs={HOUSEHOLD_SEARCH_DEBOUNCE_MS}
      getItemKey={(item) => item.house_hold_id}
      getItemLabel={getHouseholdComboboxLabel}
      isItemEqual={(item, selected) => item.house_hold_id === selected?.house_hold_id}
      placeholder="Select household"
      emptyMessage={emptyMessage}
      loadingMessage="Fetching households"
      loadingMoreMessage="Fetching more households"
      disabled={disabled}
      className={className}
      renderItem={(option) => (
        <HouseholdComboboxItem household={option as Household} />
      )}
    />
  );
}
